/**
 * КАРТКА ФІЗОСОБИ — окрема сторінка (ч.1059, рішення власника: картки
 * довідників — звичайні сторінки зі своєю адресою + чернетка незбережених
 * правок). Маршрути: /individuals/new і /individuals/:id.
 *
 * Раніше картка жила всередині списку /individuals як стан компонента:
 * «назад», оновлення сторінки чи перехід в інший розділ її закривали, а
 * чернетку незбереженого не було до чого привʼязати (усі картки ділили
 * одну адресу списку). Старі посилання /individuals?open=<id> список
 * переадресовує сюди.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { individuals, type Individual } from '@/api/hr';
import { refs } from '@/api/refs';
import { findBankByIban } from '@/shared/ukr_classifiers';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocBlock from '@/shared/components/DocBlock';
import DocLayout from '@/shared/components/DocLayout';
import DocRail from '@/shared/components/DocRail';
import { useDefaults } from '@/store/defaults';
import IndividualBankAccounts from '@/components/IndividualBankAccounts';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import {
  DraftSlotsProvider, confirmPendingRows, type DraftSlots,
} from '@/shared/hooks/draftSlots';
import { signalUserEdit } from '@/store/formDrafts';


// РНОКПП (ІПН фізособи) — лише формат, 10 цифр (дзеркалить backend
// is_valid_rnokpp; checksum прибрана рішенням власника 2026-06-02).
function isValidRnokpp(v: string): boolean {
  return /^\d{10}$/.test(v);
}

interface Editing {
  id?: string;
  needs_review?: boolean;
  last_name: string;
  first_name: string;
  middle_name: string;
  inn: string;
  birth_date: string;
  birth_place: string;
  gender: '' | 'M' | 'F';
  pf_insurance_number: string;
  citizenship_country: string;
  is_non_resident: boolean;
  passport_doc_type: string;
  passport_series: string;
  passport_number: string;
  passport_issued_by: string;
  passport_issued_at: string;
  phone: string;
  email: string;
  address: string;
  payout_iban: string;
  comment: string;  // ч.883
  payout_bank_name: string;
  // домен 2 — воїнський облік
  mil_rank: string;
  mil_vus: string;
  mil_fitness: string;
  mil_office: string;
  mil_obligation: string;
  mil_registration: string;
  mil_id_details: string;
  mil_registry_number: string;
  // домен 3 — інвалідність + сімейний стан (плоско)
  has_disability: boolean;
  disability_group: string;
  disability_cert_series: string;
  disability_cert_number: string;
  disability_cert_issued_at: string;
  disability_from: string;
  disability_to: string;
  disability_double_count: boolean;
  marital_status: string;
  // домен 4 — пенсіонер (плоско)
  is_pensioner: boolean;
  pension_cert_series: string;
  pension_cert_number: string;
  pension_cert_issued_at: string;
}

// Домен 3 — рядок складу родини у формі (UI-стан).
interface FamilyRow {
  kinship_degree: string;
  member_full_name: string;
  birth_year: string;   // input — рядок, у payload → number|null
  is_dependent: boolean;
}

// Домен 4 — рядок початкового стажу у формі (UI-стан).
interface StazhRow {
  stazh_kind: string;
  stazh_date: string;
  work_place: string;
  days_count: string;   // input — рядок, у payload → number|null
}

const EMPTY: Editing = {
  last_name: '', first_name: '', middle_name: '',
  inn: '', birth_date: '', birth_place: '', gender: '',
  pf_insurance_number: '', citizenship_country: 'Україна', is_non_resident: false,
  comment: '',
  passport_doc_type: '', passport_series: '', passport_number: '',
  passport_issued_by: '', passport_issued_at: '',
  phone: '', email: '', address: '',
  payout_iban: '', payout_bank_name: '',
  mil_rank: '', mil_vus: '', mil_fitness: '', mil_office: '',
  mil_obligation: '', mil_registration: '', mil_id_details: '', mil_registry_number: '',
  has_disability: false, disability_group: '', disability_cert_series: '',
  disability_cert_number: '', disability_cert_issued_at: '',
  disability_from: '', disability_to: '', disability_double_count: false, marital_status: '',
  is_pensioner: false, pension_cert_series: '', pension_cert_number: '',
  pension_cert_issued_at: '',
};

type CardTab = 'main' | 'docs' | 'contacts' | 'pension'
  | 'disability' | 'military' | 'family' | 'stazh';

/** Збережена фізособа → стан форми картки. */
function toEditing(i: Individual): Editing {
  return {
    id: i.id,
    needs_review: i.needs_review,
    last_name: i.last_name, first_name: i.first_name,
    middle_name: i.middle_name ?? '',
    inn: i.inn ?? '', birth_date: i.birth_date ?? '',
    birth_place: i.birth_place ?? '',
    gender: i.gender ?? '',
    pf_insurance_number: i.pf_insurance_number ?? '',
    citizenship_country: i.citizenship_country ?? '',
    is_non_resident: i.is_non_resident ?? false,
    comment: i.comment ?? '',
    passport_doc_type: i.passport_doc_type ?? '',
    passport_series: i.passport_series ?? '',
    passport_number: i.passport_number ?? '',
    passport_issued_by: i.passport_issued_by ?? '',
    passport_issued_at: i.passport_issued_at ?? '',
    phone: i.phone ?? '', email: i.email ?? '', address: i.address ?? '',
    payout_iban: i.payout_iban ?? '', payout_bank_name: i.payout_bank_name ?? '',
    mil_rank: i.military?.rank ?? '', mil_vus: i.military?.vus ?? '',
    mil_fitness: i.military?.fitness ?? '', mil_office: i.military?.military_office ?? '',
    mil_obligation: i.military?.obligation_relation ?? '',
    mil_registration: i.military?.registration_relation ?? '',
    mil_id_details: i.military?.military_id_details ?? '',
    mil_registry_number: i.military?.registry_record_number ?? '',
    has_disability: i.has_disability ?? false,
    disability_group: i.disability_group ?? '',
    disability_cert_series: i.disability_cert_series ?? '',
    disability_cert_number: i.disability_cert_number ?? '',
    disability_cert_issued_at: i.disability_cert_issued_at ?? '',
    disability_from: i.disability_from ?? '',
    disability_to: i.disability_to ?? '',
    disability_double_count: i.disability_double_count ?? false,
    marital_status: i.marital_status ?? '',
    is_pensioner: i.is_pensioner ?? false,
    pension_cert_series: i.pension_cert_series ?? '',
    pension_cert_number: i.pension_cert_number ?? '',
    pension_cert_issued_at: i.pension_cert_issued_at ?? '',
  };
}

function toFamily(i: Individual): FamilyRow[] {
  return (i.family ?? []).map((f) => ({
    kinship_degree: f.kinship_degree ?? '',
    member_full_name: f.member_full_name ?? '',
    birth_year: f.birth_year != null ? String(f.birth_year) : '',
    is_dependent: f.is_dependent,
  }));
}

function toStazh(i: Individual): StazhRow[] {
  return (i.stazh ?? []).map((s) => ({
    stazh_kind: s.stazh_kind ?? '',
    stazh_date: s.stazh_date ?? '',
    work_place: s.work_place ?? '',
    days_count: s.days_count != null ? String(s.days_count) : '',
  }));
}

/** Що зберігає чернетка картки: реквізити + таблиці родини і стажу +
 *  слоти вкладок (рядок нового банк-рахунку до «додати», ч.1060). */
interface CardDraft {
  editing: Editing;
  family: FamilyRow[];
  familyDirty: boolean;
  stazh: StazhRow[];
  stazhDirty: boolean;
  slots?: DraftSlots;
}

export default function IndividualCardPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  // Справочник банків НБУ — авто-назва банку з МФО IBAN (як у картці організації).
  const banksQ = useQuery({ queryKey: ['banks'], queryFn: () => refs.banks() });
  // Вкладення картки фізособи (2026-08-13): фізособа tenant-scoped (без
  // організації) — файли привʼязуємо до дефолтної org з шапки.
  const attachOrgId = useDefaults((st) => st.defaultOrgId);
  const indQ = useQuery({
    queryKey: ['individual', id],
    queryFn: () => individuals.get(id!),
    enabled: !isNew,
  });
  const [editing, setEditing] = useState<Editing>(EMPTY);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [family, setFamily] = useState<FamilyRow[]>([]);
  const [familyDirty, setFamilyDirty] = useState(false);
  const [stazh, setStazh] = useState<StazhRow[]>([]);
  const [stazhDirty, setStazhDirty] = useState(false);
  // «Основне» — перша вкладка (рішення власника 2026-08-19); ч.1059: і нова,
  // і збережена картка відкриваються саме на ній (раніше — на «Документи»).
  const [tab, setTab] = useState<CardTab>('main');
  const [error, setError] = useState<string | null>(null);

  // Заповнити форму ПІСЛЯ завантаження (раз на id).
  const loaded = indQ.data;
  if (loaded && loadedId !== loaded.id) {
    setLoadedId(loaded.id);
    setEditing(toEditing(loaded));
    setFamily(toFamily(loaded));
    setFamilyDirty(false);
    setStazh(toStazh(loaded));
    setStazhDirty(false);
  }

  // ч.1059: чернетка незбережених правок (як у документах): живе лише у
  // браузері, при поверненні пропонує відновити. Вкладені мутації (рахунки,
  // вкладення) картку не зберігають — тому явний markSaved.
  const [slots, setSlots] = useState<DraftSlots>({});
  const draftValue = useMemo<CardDraft>(
    () => ({ editing, family, familyDirty, stazh, stazhDirty, slots }),
    [editing, family, familyDirty, stazh, stazhDirty, slots],
  );
  const restoreDraft = useCallback((v: CardDraft) => {
    setEditing((cur) => ({ ...EMPTY, ...v.editing, id: cur.id, needs_review: cur.needs_review }));
    setFamily(v.family ?? []);
    setFamilyDirty(!!v.familyDirty);
    setStazh(v.stazh ?? []);
    setStazhDirty(!!v.stazhDirty);
    setSlots(v.slots ?? {});
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    ready: isNew || (!!loaded && loadedId === loaded.id),
    subject: 'цієї картки',
    clearOnAnyMutation: false,
  });

  // ч.1059: додати/прибрати рядок — правка користувача (чернетка картки).
  const addFamily = () => {
    signalUserEdit();
    setFamily((arr) => [...arr, { kinship_degree: '', member_full_name: '', birth_year: '', is_dependent: false }]);
    setFamilyDirty(true);
  };
  const removeFamily = (i: number) => {
    signalUserEdit();
    setFamily((arr) => arr.filter((_, idx) => idx !== i));
    setFamilyDirty(true);
  };
  const setFamilyRow = (i: number, patch: Partial<FamilyRow>) => {
    setFamily((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    setFamilyDirty(true);
  };
  const addStazh = () => {
    signalUserEdit();
    setStazh((arr) => [...arr, { stazh_kind: '', stazh_date: '', work_place: '', days_count: '' }]);
    setStazhDirty(true);
  };
  const removeStazh = (i: number) => {
    signalUserEdit();
    setStazh((arr) => arr.filter((_, idx) => idx !== i));
    setStazhDirty(true);
  };
  const setStazhRow = (i: number, patch: Partial<StazhRow>) => {
    setStazh((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    setStazhDirty(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        last_name: editing.last_name,
        comment: editing.comment.trim() || null,
        first_name: editing.first_name,
        middle_name: editing.middle_name || null,
        inn: editing.inn || null,
        birth_date: editing.birth_date || null,
        birth_place: editing.birth_place || null,
        gender: editing.gender || null,
        pf_insurance_number: editing.pf_insurance_number || null,
        citizenship_country: editing.citizenship_country || null,
        is_non_resident: editing.is_non_resident,
        passport_doc_type: editing.passport_doc_type || null,
        passport_series: editing.passport_series || null,
        passport_number: editing.passport_number || null,
        passport_issued_by: editing.passport_issued_by || null,
        passport_issued_at: editing.passport_issued_at || null,
        phone: editing.phone || null,
        email: editing.email || null,
        address: editing.address || null,
        payout_iban: editing.payout_iban || null,
        // Назва банку — авто з МФО IBAN (справочник НБУ), не ручний ввід.
        payout_bank_name: findBankByIban(editing.payout_iban, banksQ.data ?? []),
        // домен 3 плоско: інвалідність + сімейний стан
        has_disability: editing.has_disability,
        disability_group: editing.disability_group || null,
        disability_cert_series: editing.disability_cert_series || null,
        disability_cert_number: editing.disability_cert_number || null,
        disability_cert_issued_at: editing.disability_cert_issued_at || null,
        disability_from: editing.disability_from || null,
        disability_to: editing.disability_to || null,
        disability_double_count: editing.has_disability && editing.disability_group === '2'
          ? editing.disability_double_count : false,
        marital_status: editing.marital_status || null,
        // домен 4 плоско: пенсіонер
        is_pensioner: editing.is_pensioner,
        pension_cert_series: editing.pension_cert_series || null,
        pension_cert_number: editing.pension_cert_number || null,
        pension_cert_issued_at: editing.pension_cert_issued_at || null,
      };
      return (async () => {
        const ind = editing.id
          ? await individuals.update(editing.id, payload)
          : await individuals.create(payload);
        // Домен 2: воїнський облік — окремий upsert якщо хоч одне поле заповнене.
        const e = editing;
        const hasMil = e.mil_rank || e.mil_vus || e.mil_fitness || e.mil_office
          || e.mil_obligation || e.mil_registration || e.mil_id_details
          || e.mil_registry_number;
        if (hasMil) {
          await individuals.upsertMilitary(ind.id, {
            rank: e.mil_rank || null, vus: e.mil_vus || null,
            fitness: e.mil_fitness || null, military_office: e.mil_office || null,
            obligation_relation: e.mil_obligation || null,
            registration_relation: e.mil_registration || null,
            military_id_details: e.mil_id_details || null,
            registry_record_number: e.mil_registry_number || null,
          });
        }
        // Домен 3: склад родини — replace якщо список редагувався (порожній теж
        // відправляємо — щоб очистити видалені рядки).
        if (familyDirty) {
          await individuals.replaceFamily(ind.id, family.map((r, idx) => ({
            line_number: idx + 1,
            kinship_degree: r.kinship_degree || null,
            member_full_name: r.member_full_name || null,
            member_individual_id: null,
            birth_year: r.birth_year ? Number(r.birth_year) : null,
            is_dependent: r.is_dependent,
          })));
        }
        // Домен 4: початковий стаж — replace якщо список редагувався.
        if (stazhDirty) {
          await individuals.replaceStazh(ind.id, stazh.map((r, idx) => ({
            line_number: idx + 1,
            stazh_kind: r.stazh_kind || null,
            stazh_date: r.stazh_date || null,
            work_place: r.work_place || null,
            days_count: r.days_count ? Number(r.days_count) : null,
          })));
        }
        return ind;
      })();
    },
    onSuccess: () => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['individuals'] });
      qc.invalidateQueries({ queryKey: ['individual', id] });
      navigate('/individuals');
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (rid: string) => individuals.remove(rid),
    onSuccess: () => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['individuals'] });
      navigate('/individuals');
    },
    onError: (e) => setError(extractError(e)),
  });

  if (!isNew && indQ.isLoading) {
    return <div className="card"><p>Завантаження…</p></div>;
  }
  if (!isNew && indQ.isError) {
    return (
      <div className="card">
        <p>Фізособу не знайдено.</p>
        <button onClick={() => navigate('/individuals')}>до списку</button>
      </div>
    );
  }

  return (
    <DraftSlotsProvider slots={slots} setSlots={setSlots}>
    <div className="card">
      {/* Картка — на всю сторінку (рішення власника 2026-08-19), тепер зі
          своєю адресою (ч.1059). */}
      <div>
        <div className="toolbar with-filters">
          <h2 style={{ margin: 0 }}>
            {editing.id
              ? `Фізособа: ${editing.last_name} ${editing.first_name}`.trim()
              : 'Нова фізособа'}
          </h2>
          {/* ч.642: канон DocumentToolbar (плашка дій + бейджі стану). */}
          <DocumentToolbar
            onBackToList={() => navigate('/individuals')}
            onSave={() => {
              // ч.1060: недодані рядки (новий банк-рахунок) — питаємо, щоб не
              // загубились мовчки: після збереження картка закривається.
              void confirmPendingRows(slots, confirm).then((ok) => { if (ok) save.mutate(); });
            }}
            savePending={save.isPending}
            saveDisabled={save.isPending
              || (!!editing.inn && !isValidRnokpp(editing.inn))
              || (!editing.id && (!isValidRnokpp(editing.inn) || !editing.birth_date))
              || (editing.has_disability && !editing.disability_from)}
            postable={false}
            isNew={!editing.id}
            onDelete={() => { confirm('Видалити фізособу?').then((ok) => {
              if (ok && editing.id) remove.mutate(editing.id);
            }); }}
            // ч.809: довідник — лише «історія» (аудит-тригер 0499), без дт-кт/звʼязаних.
            historyDoc={editing.id ? { type: 'ref.individuals', id: editing.id } : undefined}
            railMode
            extraStatusBadges={editing.id && editing.needs_review
              ? [{ label: 'потребує перевірки', tone: 'amber',
                   title: 'Картка у карантині міграції — виправте реквізити' }] : []}
          />
        </div>
        {editing.id && editing.needs_review && (
          <div style={{
            background: 'var(--badge-amber-bg)', border: '1px solid #fde68a', color: 'var(--badge-amber-fg)',
            borderRadius: 6, padding: '0.6rem 0.8rem', marginBottom: '0.75rem',
            fontSize: '0.88rem' }}>
            ⚠ <b>Картка у карантині міграції.</b> Реквізити з 1С не відповідають
            стандарту (напр. дубль-картка з одним ІПН або битий РНОКПП). Виправте
            ІПН/реквізити та збережіть — картка «оживе» і стане доступною у документах.
          </div>
        )}
        {/* ч.885: права колонка — «Історія документа» (довідник — без звʼязаних). */}
        <DocLayout rail={
          <DocRail isNew={!editing.id} related={false}
            doc={editing.id ? { type: 'ref.individuals', id: editing.id } : undefined} />
        }>
        <div>
        {/* Вкладки — ЗВЕРХУ (як у картці організації), «Основне» перша. */}
        <div className="seg-tabs">
          {([
            ['main', 'Основне'],
            ['docs', 'Документи'],
            ['contacts', 'Контакти'],
            ['pension', 'Пенсіонер'],
            ['disability', 'Інвалідність'],
            ['military', 'Військовий облік'],
            ['family', 'Сімейний стан'],
            ['stazh', 'Стаж'],
          ] as const).map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={tab === key ? 'active' : ''}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'main' && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Прізвище *</label>
            <input value={editing.last_name}
                   onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Ім'я *</label>
            <input value={editing.first_name}
                   onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>По батькові</label>
            <input value={editing.middle_name}
                   onChange={(e) => setEditing({ ...editing, middle_name: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>ІПН{!editing.id ? ' *' : ''}</label>
            <input value={editing.inn} maxLength={10} placeholder="1234567890"
                   style={editing.inn && !isValidRnokpp(editing.inn)
                     ? { borderColor: '#d33' } : undefined}
                   onChange={(e) => setEditing({
                     ...editing, inn: e.target.value.replace(/\D/g, ''),
                   })} />
            {editing.inn ? (
              <small style={{ color: isValidRnokpp(editing.inn) ? 'green' : '#d33' }}>
                {isValidRnokpp(editing.inn)
                  ? '✓ Формат правильний'
                  : `✗ Має бути 10 цифр (зараз ${editing.inn.length})`}
              </small>
            ) : !editing.id ? (
              <small style={{ color: '#d33' }}>✗ Обов'язкове поле</small>
            ) : null}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Дата народження{!editing.id ? ' *' : ''}</label>
            <DatePopover value={editing.birth_date}
                   style={!editing.id && !editing.birth_date
                     ? { borderColor: '#d33' } : undefined}
                   onChange={(v) => setEditing({ ...editing, birth_date: v })} />
            {!editing.id && !editing.birth_date && (
              <small style={{ color: '#d33' }}>✗ Обов'язкове поле</small>
            )}
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Стать</label>
            <select value={editing.gender}
                    onChange={(e) => setEditing({ ...editing, gender: e.target.value as '' | 'M' | 'F' })}>
              <option value="">—</option>
              <option value="M">Чоловіча</option>
              <option value="F">Жіноча</option>
            </select>
          </div>
          {/* Реквізити для виплат — на головній формі (телефон/email/адреса — у вкладці «Контакти»).
              ч.811: для збереженої фізособи — таблиця рахунків (кілька; основний =
              виплата ЗП); для нової — один IBAN, що стане основним рахунком. */}
          {editing.id && (
            <div style={{ gridColumn: 'span 3' }}>
              <IndividualBankAccounts individualId={editing.id} />
            </div>
          )}
          {!editing.id && (<>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>
              IBAN
              {editing.payout_iban && (
                isValidIbanTs(editing.payout_iban)
                  ? <span style={{ color: 'var(--badge-green-fg)', marginLeft: 6 }}>✓</span>
                  : <span style={{ color: '#dc2626', marginLeft: 6 }}>
                      ✗ {editing.payout_iban.replace(/\s/g, '').length}/29 символів
                    </span>
              )}
            </label>
            <input value={editing.payout_iban}
                   maxLength={34}
                   placeholder="UA213223130000026007233566001"
                   onChange={(e) => setEditing({ ...editing, payout_iban: sanitizeIban(e.target.value) })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 8' }}>
            {/* Назва банку — авто з МФО IBAN (справочник НБУ), read-only. */}
            <label>Банк</label>
            <input
              value={findBankByIban(editing.payout_iban, banksQ.data ?? []) ?? '—'}
              readOnly tabIndex={-1}
              style={{ background: 'var(--bg-soft)', color: 'var(--text-secondary)' }} />
          </div>
          </>)}
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Страховий номер ПФУ</label>
            <input value={editing.pf_insurance_number}
                   onChange={(e) => setEditing({ ...editing, pf_insurance_number: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4', display: 'flex', alignItems: 'flex-end', gap: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={editing.is_non_resident}
                     onChange={(e) => setEditing({ ...editing, is_non_resident: e.target.checked })} />
              Нерезидент
            </label>
          </div>
          {editing.is_non_resident && (
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Громадянство (країна)</label>
              <input value={editing.citizenship_country}
                     onChange={(e) => setEditing({ ...editing, citizenship_country: e.target.value })} />
            </div>
          )}
        </div>
        )}


        {/* --- Вкладка ДОКУМЕНТИ: паспорт + пенсіонер + інвалідність --- */}
        {tab === 'docs' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <div style={{ gridColumn: 'span 3', fontWeight: 600, fontSize: '0.85em',
                        color: 'var(--muted)', marginTop: '0.25rem' }}>
            Паспорт
          </div>
          <div className="field">
            <label>Вид документа</label>
            <input value={editing.passport_doc_type}
                   onChange={(e) => setEditing({ ...editing, passport_doc_type: e.target.value })} />
          </div>
          <div className="field">
            <label>Серія</label>
            <input value={editing.passport_series} maxLength={10}
                   onChange={(e) => setEditing({ ...editing, passport_series: e.target.value })} />
          </div>
          <div className="field">
            <label>Номер</label>
            <input value={editing.passport_number} maxLength={20}
                   onChange={(e) => setEditing({ ...editing, passport_number: e.target.value })} />
          </div>
          <div className="field">
            <label>Дата видачі</label>
            <DatePopover value={editing.passport_issued_at}
                   onChange={(v) => setEditing({ ...editing, passport_issued_at: v })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Ким виданий</label>
            <input value={editing.passport_issued_by}
                   onChange={(e) => setEditing({ ...editing, passport_issued_by: e.target.value })} />
          </div>
          </div>
        )}

        {/* --- Вкладка КОНТАКТИ: телефон + email + адреса --- */}
        {tab === 'contacts' && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Телефон</label>
            <input value={editing.phone}
                   onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Email</label>
            <input value={editing.email}
                   onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 12' }}>
            <label>Адреса</label>
            <input value={editing.address}
                   onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
          </div>
        </div>
        )}

        {/* --- Вкладка ПЕНСIОНЕР: поля активні лише при галці «Пенсіонер» --- */}
        {tab === 'pension' && (
        <div className="doc-grid" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end', gap: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={editing.is_pensioner}
                     onChange={(e) => setEditing({ ...editing, is_pensioner: e.target.checked })} />
              Пенсіонер
            </label>
          </div>
          <div className="field" style={{ gridColumn: 'span 4' }}>
            <label>Посвідчення — серія</label>
            <input value={editing.pension_cert_series} disabled={!editing.is_pensioner}
                   onChange={(e) => setEditing({ ...editing, pension_cert_series: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Посвідчення — номер</label>
            <input value={editing.pension_cert_number} disabled={!editing.is_pensioner}
                   onChange={(e) => setEditing({ ...editing, pension_cert_number: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата видачі</label>
            <DatePopover value={editing.pension_cert_issued_at} disabled={!editing.is_pensioner}
                   onChange={(v) => setEditing({ ...editing, pension_cert_issued_at: v })} />
          </div>
        </div>
        )}

        {/* --- Вкладка IНВАЛIДНIСТЬ: поля активні лише при галці «Інвалідність» --- */}
        {tab === 'disability' && (
        <div className="doc-grid" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'flex-end', gap: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={editing.has_disability}
                     onChange={(e) => setEditing({ ...editing, has_disability: e.target.checked })} />
              Має інвалідність
            </label>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Iнвалідність діє з{editing.has_disability ? ' *' : ''}</label>
            <DatePopover value={editing.disability_from} disabled={!editing.has_disability}
                   onChange={(v) => setEditing({ ...editing, disability_from: v })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Iнвалідність діє по</label>
            <DatePopover value={editing.disability_to} disabled={!editing.has_disability}
                   onChange={(v) => setEditing({ ...editing, disability_to: v })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'flex-end' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Пільга ЄСВ 8.41% з дати «діє з»; порожньо «діє по» = безстроково.
            </span>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Група</label>
            <select value={editing.disability_group} disabled={!editing.has_disability}
                    onChange={(e) => setEditing({ ...editing, disability_group: e.target.value })}>
              <option value="">—</option>
              <option value="1">I група</option>
              <option value="2">II група</option>
              <option value="3">III група</option>
            </select>
          </div>
          {/* ч.1208: норматив працевлаштування — І група завжди зараховується як два
              робочих місця; ІІ група — лише з порушенням зору або психічними розладами. */}
          <div className="field" style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              title="Для нормативу працевлаштування осіб з інвалідністю така особа зараховується як два робочих місця. І група — завжди, незалежно від причини.">
              <input type="checkbox"
                checked={editing.disability_group === '1' || (editing.disability_group === '2' && editing.disability_double_count)}
                disabled={!editing.has_disability || editing.disability_group !== '2'}
                onChange={(e) => setEditing({ ...editing, disability_double_count: e.target.checked })} />
              ІІ група: порушення зору або психічні розлади
            </label>
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Посвідчення — серія</label>
            <input value={editing.disability_cert_series} disabled={!editing.has_disability}
                   onChange={(e) => setEditing({ ...editing, disability_cert_series: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Посвідчення — номер</label>
            <input value={editing.disability_cert_number} disabled={!editing.has_disability}
                   onChange={(e) => setEditing({ ...editing, disability_cert_number: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Посвідчення — дата видачі</label>
            <DatePopover value={editing.disability_cert_issued_at} disabled={!editing.has_disability}
                   onChange={(v) => setEditing({ ...editing, disability_cert_issued_at: v })} />
          </div>
        </div>
        )}

        {tab === 'military' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <div style={{ gridColumn: 'span 3', fontWeight: 600, fontSize: '0.85em',
                        color: 'var(--muted)', marginTop: '0.25rem' }}>
            Військовий облік
          </div>
          <div className="field">
            <label>Військове звання</label>
            <input value={editing.mil_rank}
                   onChange={(e) => setEditing({ ...editing, mil_rank: e.target.value })} />
          </div>
          <div className="field">
            <label>ВОС</label>
            <input value={editing.mil_vus}
                   onChange={(e) => setEditing({ ...editing, mil_vus: e.target.value })} />
          </div>
          <div className="field">
            <label>Придатність</label>
            <input value={editing.mil_fitness}
                   onChange={(e) => setEditing({ ...editing, mil_fitness: e.target.value })} />
          </div>
          <div className="field">
            <label>Військкомат</label>
            <input value={editing.mil_office}
                   onChange={(e) => setEditing({ ...editing, mil_office: e.target.value })} />
          </div>
          <div className="field">
            <label>Відношення до військового обов'язку</label>
            <select value={editing.mil_obligation}
                    onChange={(e) => setEditing({ ...editing, mil_obligation: e.target.value })}>
              <option value="">—</option>
              <option value="військовозобов'язаний">військовозобов'язаний</option>
              <option value="не військовозобов'язаний">не військовозобов'язаний</option>
              <option value="призовник">призовник</option>
              {editing.mil_obligation
                && !["військовозобов'язаний", "не військовозобов'язаний", 'призовник'].includes(editing.mil_obligation)
                && <option value={editing.mil_obligation}>{editing.mil_obligation} (з 1С)</option>}
            </select>
          </div>
          <div className="field">
            <label>Реквізити військового квитка</label>
            <input value={editing.mil_id_details}
                   onChange={(e) => setEditing({ ...editing, mil_id_details: e.target.value })} />
          </div>
          <div className="field">
            <label>№ запису в реєстрі військовозобов'язаних</label>
            <input value={editing.mil_registry_number}
                   onChange={(e) => setEditing({ ...editing, mil_registry_number: e.target.value })} />
          </div>
          </div>
        )}

        {tab === 'family' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <div style={{ gridColumn: 'span 3', fontWeight: 600, fontSize: '0.85em',
                        color: 'var(--muted)', marginTop: '0.25rem' }}>
            Сімейний стан
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Сімейний стан</label>
            <input value={editing.marital_status} maxLength={100}
                   placeholder="Одружений / Неодружена / Розлучений…"
                   onChange={(e) => setEditing({ ...editing, marital_status: e.target.value })} />
          </div>

          <div style={{ gridColumn: 'span 3', fontWeight: 600, fontSize: '0.85em',
                        color: 'var(--muted)', marginTop: '0.25rem' }}>
            Склад сім'ї
          </div>
          <div style={{ gridColumn: 'span 3' }}>
            <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th>Ступінь споріднення</th>
                  <th>ПІБ члена сім'ї</th>
                  <th style={{ width: 90 }}>Рік нар.</th>
                  <th style={{ width: 90, textAlign: 'center' }}>Утриманець</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {family.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <input value={r.kinship_degree} style={{ width: '100%' }}
                             placeholder="Дитина / Дружина…"
                             onChange={(e) => setFamilyRow(i, { kinship_degree: e.target.value })} />
                    </td>
                    <td>
                      <input value={r.member_full_name} style={{ width: '100%' }}
                             onChange={(e) => setFamilyRow(i, { member_full_name: e.target.value })} />
                    </td>
                    <td>
                      <input type="number" value={r.birth_year} style={{ width: 80 }}
                             onChange={(e) => setFamilyRow(i, { birth_year: e.target.value })} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={r.is_dependent}
                             onChange={(e) => setFamilyRow(i, { is_dependent: e.target.checked })} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" title="Видалити" onClick={() => removeFamily(i)}>×</button>
                    </td>
                  </tr>
                ))}
                {family.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: 'var(--muted)' }}>Членів сім'ї не додано.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <button type="button" onClick={addFamily} style={{ marginTop: '0.5rem' }}>
              + Додати члена сім'ї
            </button>
          </div>
          </div>
        )}

        {tab === 'stazh' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <div style={{ gridColumn: 'span 3', fontWeight: 600, fontSize: '0.85em',
                        color: 'var(--muted)', marginTop: '0.25rem' }}>
            Початковий стаж
          </div>
          <div style={{ gridColumn: 'span 3' }}>
            <table className="data-table" style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th>Вид стажу</th>
                  <th style={{ width: 130 }}>Дата</th>
                  <th>Місце роботи</th>
                  <th style={{ width: 90 }}>Днів</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {stazh.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <input value={r.stazh_kind} style={{ width: '100%' }}
                             placeholder="Загальний / Страховий…"
                             onChange={(e) => setStazhRow(i, { stazh_kind: e.target.value })} />
                    </td>
                    <td>
                      <DatePopover value={r.stazh_date} style={{ width: 120 }}
                             onChange={(v) => setStazhRow(i, { stazh_date: v })} />
                    </td>
                    <td>
                      <input value={r.work_place} style={{ width: '100%' }}
                             onChange={(e) => setStazhRow(i, { work_place: e.target.value })} />
                    </td>
                    <td>
                      <input type="number" value={r.days_count} style={{ width: 80 }}
                             onChange={(e) => setStazhRow(i, { days_count: e.target.value })} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button type="button" title="Видалити" onClick={() => removeStazh(i)}>×</button>
                    </td>
                  </tr>
                ))}
                {stazh.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: 'var(--muted)' }}>Записів стажу не додано.</td>
                  </tr>
                )}
              </tbody>
            </table>
            <button type="button" onClick={addStazh} style={{ marginTop: '0.5rem' }}>
              + Додати запис стажу
            </button>
          </div>
        </div>
        )}

        {/* ч.883: блок «Коментарі та вкладення» (без номера). Вкладення
            (2026-08-13): скани документів фізособи; привʼязка — дефолтна орг. */}
        <div style={{ marginTop: '0.75rem' }}>
        <DocBlock title="Коментарі та вкладення">
          <div className="docblock__cols">
            <div className="field" style={{ margin: 0 }}>
              <label>Коментар</label>
              <input value={editing.comment}
                     onChange={(e) => setEditing({ ...editing, comment: e.target.value })} />
            </div>
            <div>
              {editing?.id && attachOrgId && (
                <AttachmentsPanel organizationId={attachOrgId}
                  documentType={'ref.individuals'} documentId={editing.id} />
              )}
            </div>
          </div>
        </DocBlock>
        </div>
        </div>
        </DocLayout>
        {error && <div className="error" style={{ marginTop: '0.75rem', color: 'var(--danger)' }}>{error}</div>}
      </div>
    </div>
    </DraftSlotsProvider>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

// Дзеркало backend is_valid_iban (mod-97-10, Норма #20) — live ✓/✗ без round-trip.
// М'яка перевірка IBAN — ЛИШЕ формат+довжина (БЕЗ mod-97, дзеркало backend
// is_valid_iban_format). Для UA — 29 символів: UA + 27 цифр.
function isValidIbanTs(value: string): boolean {
  const v = value.replace(/\s/g, '').toUpperCase();
  if (v.length < 15 || v.length > 34) return false;
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(v)) return false;
  if (v.startsWith('UA') && (v.length !== 29 || !/^\d+$/.test(v.slice(4)))) return false;
  return true;
}

// Фільтр вводу IBAN: лише латиниця+цифри, верхній регістр, max 34 (UA — 29).
function sanitizeIban(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 34);
}
